// helpdesk-frontend/src/context/UIContext.js

import React, { createContext, useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { SZEROKOSC_TELEFONU } from "../utils/urzadzenieMobilne";
// [WYDAJNOŚĆ 2026-09-12] Widoczność modułów pobiera się RAZ na sesję — wspólnie dla tego kontekstu,
// `AuthContext` i `Sidebara`. Powód i zasady unieważniania: nagłówek `context/AuthContext.js`.
import {
    pobierzWidocznoscModulow,
    wyczyscPamiecWidocznosciModulow,
    subskrybujWidocznoscModulow,
    wersjaPamieciWidocznosciModulow,
} from "./AuthContext";

// [zgłoszenie właściciela 2026-09-16] Granica telefonu mieszka w `utils/urzadzenieMobilne`, bo
// potrzebuje jej też osłona trasy, która NIE MA dostępu do tego kontekstu. Jedna liczba, dwa odczyty.
const MOBILE_BREAKPOINT = SZEROKOSC_TELEFONU;

export const UIContext = createContext();

export const UIProvider = ({ children }) => {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > MOBILE_BREAKPOINT);

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebar-collapsed");
        return saved === "true";
    });

    const [isNewTicketModalOpen, setIsNewTicketModalOpen] = useState(false);
    const [preselectedTicketType, setPreselectedTicketType] = useState(null);
    /* [G-1 2026-09-11] Wstepnie wypelniony temat i opis nowego zgloszenia ("zglos poprawke"
       przy sprzecie na pulpicie). STAN BYL UZYWANY, ALE NIGDY ZADEKLAROWANY — `openNewTicketModal`
       wolal `setPreselectedTicketData`, ktorego nie bylo, wiec klikniecie "Zglos problem"
       wywracalo sie na `ReferenceError`. Zaden test nie montuje tego dostawcy z tym przyciskiem,
       a `no-undef` jest bledem dopiero w buildzie produkcyjnym — stad przeszlo przez suity. */
    const [preselectedTicketData, setPreselectedTicketData] = useState(null);
    const [isQuickAccessOpen, setIsQuickAccessOpen] = useState(false);

    // ── Widget „Nowe zgłoszenie" (BRIEF-0003) — widoczność sterowana ModuleVisibility 'ticketCreate' ──
    const [isTicketCreateVisible, setIsTicketCreateVisible] = useState(true);
    const [ticketCreateModuleId, setTicketCreateModuleId] = useState(null);

    const location = useLocation();
    // UIProvider jest montowany WEWNĄTRZ AuthProvider — zmiana `user` (zalogowanie w tej
    // karcie) ponawia pobranie widoczności, którego guard sesji wcześniej nie dopuścił.
    const { user } = useAuth();

    // Pokolenie pamięci widoczności — unieważnienie (wylogowanie, zmiana uprawnień, zapis w panelu)
    // podbija je i odpala efekt niżej.
    const [wersjaWidocznosci, setWersjaWidocznosci] = useState(wersjaPamieciWidocznosciModulow);
    useEffect(() => subskrybujWidocznoscModulow(setWersjaWidocznosci), []);

    /** Odczyt widoczności widgetu z pamięci sesji — BEZ sieci, gdy ktoś już pytał. */
    const wczytajWidocznoscWidgetu = useCallback(async () => {
        // GUARD zalogowania (adaptacja BRIEF-0014): auth idzie po httpOnly cookies (brak tokenu
        // w localStorage), więc markerem sesji jest wpis "user". Bez niego NIE wołamy API —
        // inaczej strona logowania kręciłaby pętlę 401.
        if (!localStorage.getItem("user")) return;
        const { wiersze } = await pobierzWidocznoscModulow();
        const row = (wiersze || []).find((m) => m.moduleCode === "ticketCreate");
        if (row) {
            setIsTicketCreateVisible(!!row.isVisible);
            setTicketCreateModuleId(row.id);
        }
        // Awaria jest cicha jak dotąd: zostaje ostatni znany stan (`pobierzWidocznoscModulow`
        // oddaje wtedy pustą listę i NIE zapamiętuje jej, więc kolejne wejście spróbuje ponownie).
    }, []);

    /**
     * WYMUSZONE odświeżenie — woła je `pages/admin/ModuleVisibilityPage` po zapisie przełącznika.
     * [WYDAJNOŚĆ 2026-09-12] Unieważnia pamięć CAŁEJ POWŁOKI, nie tylko tego kontekstu: menu boczne
     * i bramka tras czytają tę samą pamięć, więc zapis w panelu ma je przestawić natychmiast —
     * dokładnie tak, jak działo się to wtedy, gdy każdy z trzech konsumentów pytał osobno.
     */
    const refreshTicketCreateVisibility = useCallback(async () => {
        wyczyscPamiecWidocznosciModulow();
        await wczytajWidocznoscWidgetu();
    }, [wczytajWidocznoscWidgetu]);

    // Nasłuch na pojawienie się sesji (BRIEF-0014): mount + zalogowanie w TEJ karcie
    // (zmiana `user` z AuthContext — storage event nie strzela w tej samej karcie).
    useEffect(() => {
        wczytajWidocznoscWidgetu();
    }, [wczytajWidocznoscWidgetu, user, wersjaWidocznosci]);

    // Nasłuch cross-tab (BRIEF-0014): zalogowanie w INNEJ karcie ustawia localStorage.user
    // → event `storage` → odświeżenie widoczności widgetu w tej karcie.
    // Świadomie woła wersję WYMUSZONĄ: w innej karcie mogło zalogować się INNE konto, więc pamięć
    // powłoki policzona dla poprzedniego musi zniknąć.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === "user" && e.newValue) {
                refreshTicketCreateVisibility();
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [refreshTicketCreateVisibility]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem("sidebar-collapsed", String(next));
            return next;
        });
    }, []);

    const toggleMobileSidebar = useCallback(() => {
        setIsSidebarOpen((prev) => !prev);
    }, []);

    const closeMobileSidebar = useCallback(() => {
        setIsSidebarOpen(false);
    }, []);

    /**
      * @param {number|null} type  Rodzaj zgloszenia do wstepnego wyboru.
      * @param {{temat?: string, opis?: string}|null} wstepneDane
      *   [G-1 2026-09-11] Wstepnie wypelniony temat i opis. Powstalo dla „zglos poprawke" przy
      *   sprzecie na pulpicie: pracownik ma dostac zgloszenie z JUZ WPISANYM numerem ewidencyjnym
      *   i numerem seryjnym, zamiast przepisywac je z naklejki. Kategorie i tak wybiera sam —
      *   to ona rozstrzyga routing, wiec omijanie jej byloby obejsciem regul modulu.
      */
    const openNewTicketModal = useCallback((type = null, wstepneDane = null) => {
        // Widget ukryty przez admina → otwarcie modalu jest zablokowane (BRIEF-0003).
        if (!isTicketCreateVisible) return;
        setPreselectedTicketType(type);
        setPreselectedTicketData(wstepneDane);
        setIsNewTicketModalOpen(true);
    }, [isTicketCreateVisible]);

    const closeNewTicketModal = useCallback(() => {
        setIsNewTicketModalOpen(false);
        setPreselectedTicketType(null);
        setPreselectedTicketData(null);
    }, []);

    const openQuickAccess = useCallback(() => setIsQuickAccessOpen(true), []);
    const closeQuickAccess = useCallback(() => setIsQuickAccessOpen(false), []);

    // [S6, polecenie właściciela 2026-09-13] Wyszukiwarka globalna (okno + szuflada wyników). Na telefonie
    // okna nie ma — lupa w nagłówku otwiera „Szybki dostęp" (decyzja w Header.js po `isMobile`).
    const [isWyszukiwarkaOpen, setIsWyszukiwarkaOpen] = useState(false);
    const openWyszukiwarke = useCallback(() => { setIsQuickAccessOpen(false); setIsWyszukiwarkaOpen(true); }, []);
    const closeWyszukiwarke = useCallback(() => setIsWyszukiwarkaOpen(false), []);

    // ── Responsive: śledź czy jest mobile ──
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            if (mobile) {
                setIsSidebarOpen(false);
            } else {
                setIsSidebarOpen(true);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ── Zamknij sidebar na mobile po zmianie route ──
    useEffect(() => {
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    }, [location.pathname, isMobile]);

    const value = useMemo(() => ({
        isMobile,
        isSidebarOpen,
        isSidebarCollapsed,
        toggleSidebar,
        toggleMobileSidebar,
        closeMobileSidebar,
        isNewTicketModalOpen,
        openNewTicketModal,
        closeNewTicketModal,
        preselectedTicketType,
        preselectedTicketData,
        isQuickAccessOpen,
        openQuickAccess,
        closeQuickAccess,
        isWyszukiwarkaOpen,
        openWyszukiwarke,
        closeWyszukiwarke,
        isTicketCreateVisible,
        ticketCreateModuleId,
        refreshTicketCreateVisibility,
    }), [
        isMobile, isSidebarOpen, isSidebarCollapsed,
        toggleSidebar, toggleMobileSidebar, closeMobileSidebar,
        isNewTicketModalOpen, openNewTicketModal, closeNewTicketModal,
        preselectedTicketType, preselectedTicketData, isQuickAccessOpen, openQuickAccess, closeQuickAccess,
        isWyszukiwarkaOpen, openWyszukiwarke, closeWyszukiwarke,
        isTicketCreateVisible, ticketCreateModuleId, refreshTicketCreateVisibility,
    ]);

    return (
        <UIContext.Provider value={value}>
            {children}
        </UIContext.Provider>
    );
};
